import { decodeJwt } from "./utils/jwt.js";
import { fetchHandler, isErrorResponse, XRPC, XRPCError } from "./xrpc.js";
/** Authentication/session management middleware */ export class BskyAuth {
  #rpc;
  #refreshSessionPromise;
  #onRefresh;
  #onExpired;
  /** Current session state */ session;
  constructor(rpc, { onRefresh, onExpired } = {}){
    this.#rpc = rpc;
    this.#onRefresh = onRefresh;
    this.#onExpired = onExpired;
    rpc.hook((next)=>async (request)=>{
        await this.#refreshSessionPromise;
        let res = await next(this.#decorateRequest(request));
        if (isErrorResponse(res.body, [
          'ExpiredToken'
        ]) && this.session?.refreshJwt) {
          await this.#refreshSession();
          if (this.session) {
            // retry fetch
            res = await next(this.#decorateRequest(request));
          }
        }
        return res;
      });
  }
  #decorateRequest(req) {
    const session = this.session;
    if (session && !req.headers['Authorization']) {
      return {
        ...req,
        service: session.pdsUri || req.service,
        headers: {
          ...req.headers,
          Authorization: `Bearer ${session.accessJwt}`
        }
      };
    }
    return req;
  }
  #refreshSession() {
    return this.#refreshSessionPromise ||= this.#refreshSessionInner().finally(()=>{
      this.#refreshSessionPromise = undefined;
    });
  }
  async #refreshSessionInner() {
    const session = this.session;
    if (!session || !session.refreshJwt) {
      return;
    }
    const res = await fetchHandler({
      service: session.pdsUri || this.#rpc.service,
      type: 'post',
      nsid: 'com.atproto.server.refreshSession',
      headers: {
        Authorization: `Bearer ${session.refreshJwt}`
      },
      params: {}
    });
    if (isErrorResponse(res.body, [
      'ExpiredToken',
      'InvalidToken'
    ])) {
      // failed due to a bad refresh token
      this.session = undefined;
      this.#onExpired?.(session);
    } else if (res.status === 200) {
      // succeeded, update the session
      this.#updateSession(res.body);
      this.#onRefresh?.(this.session);
    }
  }
  #updateSession(raw) {
    const didDoc = raw.didDoc;
    let pdsUri;
    if (didDoc) {
      pdsUri = getPdsEndpoint(didDoc);
    }
    return this.session = {
      accessJwt: raw.accessJwt,
      refreshJwt: raw.refreshJwt,
      handle: raw.handle,
      did: raw.did,
      pdsUri: pdsUri,
      email: raw.email,
      emailConfirmed: raw.emailConfirmed,
      emailAuthFactor: raw.emailConfirmed
    };
  }
  /**
	 * Resume a saved session
	 * @param session Session information, taken from `BskyAuth#session` after login
	 */ async resume(session) {
    const now = Date.now() / 1000 + 60 * 5;
    const refreshToken = decodeJwt(session.refreshJwt);
    if (now >= refreshToken.exp) {
      throw new XRPCError(401, {
        kind: 'InvalidToken'
      });
    }
    const accessToken = decodeJwt(session.accessJwt);
    this.session = session;
    if (now >= accessToken.exp) {
      await this.#refreshSession();
    } else {
      const promise = this.#rpc.get('com.atproto.server.getSession', {});
      promise.then((response)=>{
        const existing = this.session;
        const next = response.data;
        if (!existing) {
          return;
        }
        this.#updateSession({
          ...existing,
          ...next
        });
      });
    }
    if (!this.session) {
      throw new XRPCError(401, {
        kind: 'InvalidToken'
      });
    }
    return this.session;
  }
  /**
	 * Perform a login operation
	 * @param options Login options
	 * @returns Session data that can be saved for later
	 */ async login(options) {
    // Reset the session
    this.session = undefined;
    const res = await this.#rpc.call('com.atproto.server.createSession', {
      data: {
        identifier: options.identifier,
        password: options.password,
        authFactorToken: options.code
      }
    });
    return this.#updateSession(res.data);
  }
}
/** Moderation middleware, unstable. */ export class BskyMod {
  /** List of moderation services that gets forwarded as a header */ labelers;
  constructor(rpc, { labelers = [] } = {}){
    this.labelers = labelers;
    rpc.hook((next)=>(request)=>{
        return next({
          ...request,
          headers: {
            ...request.headers,
            'atproto-accept-labelers': this.labelers.map((labeler)=>labeler.did + (labeler.redact ? `;redact` : ``)).join(', ')
          }
        });
      });
  }
}
/** XRPC class with Bluesky lexicons */ // deno-lint-ignore no-explicit-any
export const BskyXRPC = XRPC;
/**
 * Retrieves AT Protocol PDS endpoint from the DID document, if available
 * @param doc DID document
 * @returns The PDS endpoint, if available
 */ export const getPdsEndpoint = (doc)=>{
  return getServiceEndpoint(doc, '#atproto_pds', 'AtprotoPersonalDataServer');
};
/**
 * Retrieve a service endpoint from the DID document, if available
 * @param doc DID document
 * @param serviceId Service ID
 * @param serviceType Service type
 * @returns The requested service endpoint, if available
 */ export const getServiceEndpoint = (doc, serviceId, serviceType)=>{
  const did = doc.id;
  const didServiceId = did + serviceId;
  const found = doc.service?.find((service)=>service.id === serviceId || service.id === didServiceId);
  if (!found || found.type !== serviceType || typeof found.serviceEndpoint !== 'string') {
    return undefined;
  }
  return validateUrl(found.serviceEndpoint);
};
const validateUrl = (urlStr)=>{
  let url;
  try {
    url = new URL(urlStr);
  } catch  {
    return undefined;
  }
  const proto = url.protocol;
  if (url.hostname && (proto === 'http:' || proto === 'https:')) {
    return urlStr;
  }
};
//# sourceMappingURL=mod.js.map